import React, { useState, useEffect } from 'react';
import { 
    Card, Button, Form, Row, Col, Accordion, 
    Spinner, InputGroup, Badge 
} from 'react-bootstrap';
import { Trash2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../redux/slices/userSlice';
import api from '../../api/api';
import { getCurrentUser } from '../../utils/auth';

const CurriculumMappingEditor = ({ 
    projectId, 
    curriculumDraft, 
    courseSummary: initialCourseSummary, 
    level1Name = "Module",
    level2Name = "Unit",
    level3Name = "Topic",
    projectType = "academic", 
    onApprove 
}) => {
    const dispatch = useDispatch();
    const [curriculum, setCurriculum] = useState([]);
    const [summary, setSummary] = useState("");
    const [history, setHistory] = useState([]); 
    const [saving, setSaving] = useState(false);

    // State for CO, PO, and PSO Definitions
    const [courseOutcomes, setCourseOutcomes] = useState([]); 
    const [programOutcomes, setProgramOutcomes] = useState([]);
    const [programSpecificOutcomes, setProgramSpecificOutcomes] = useState([]);

    const isCorporate = projectType === 'corporate';
    const showAcademicMapping = !isCorporate; 

    // Helper: Upgrades any older string-based LOs to objects to prevent crashes
    const sanitizeCurriculum = (currArray) => {
        if (!Array.isArray(currArray)) return [];

        const normalizeBloom = (val) => {
            if (!val) return '';
            const v = String(val).toUpperCase();
            if (v.includes('REMEMBER') || v === 'L1') return 'L1';
            if (v.includes('UNDERSTAND') || v === 'L2') return 'L2';
            if (v.includes('APPLY') || v === 'L3') return 'L3';
            if (v.includes('ANALYZE') || v === 'L4') return 'L4';
            if (v.includes('EVALUATE') || v === 'L5') return 'L5';
            if (v.includes('CREATE') || v === 'L6') return 'L6';
            return val.split(' ')[0]; 
        };

        return currArray.map(mod => ({
            ...mod,
            title: mod.title || `New ${level1Name}`,
            description: mod.description || '',
            skills_competencies: mod.skills_competencies || '',
            module_average_cognitive_score: mod.module_average_cognitive_score || '', 
            children: mod.children || [],
            learning_outcomes: (mod.learning_outcomes || []).map(lo => 
                typeof lo === 'string' ? { text: lo, bloom_level: '', mapped_co: '', mapped_po: '', mapped_pso: '' } : {
                    text: lo.learning_outcome || lo.text || lo.description || '',
                    bloom_level: normalizeBloom(lo.blooms_level || lo.bloom_level || ''),
                    mapped_co: lo.mapped_co || lo.co || '',
                    mapped_po: lo.mapped_po || lo.po || '',
                    mapped_pso: lo.mapped_pso || lo.pso || ''
                }
            )
        }));
    };

    useEffect(() => {
        if (curriculumDraft) {
            const parsed = typeof curriculumDraft === 'string' ? JSON.parse(curriculumDraft) : curriculumDraft;
            
            // Reverted to expect the flat array
            const actualCurriculum = Array.isArray(parsed) ? parsed : (parsed.curriculum || []);
            setCurriculum(sanitizeCurriculum(actualCurriculum));
            
            if (!initialCourseSummary && (parsed.course_summary || parsed.summary)) {
                setSummary(parsed.course_summary || parsed.summary);
            } else if (initialCourseSummary) {
                setSummary(initialCourseSummary);
            }

            // Extract our hidden backup if it exists inside the first module
            if (actualCurriculum.length > 0 && actualCurriculum[0]._outcomes_backup) {
                const backup = actualCurriculum[0]._outcomes_backup;
                setCourseOutcomes(backup.courseOutcomes || []);
                setProgramOutcomes(backup.programOutcomes || []);
                setProgramSpecificOutcomes(backup.programSpecificOutcomes || []);
                if (backup.courseSummary) {
                    setSummary(backup.courseSummary);
                }
            }
        } else if (initialCourseSummary) {
            setSummary(initialCourseSummary);
        }
    }, [curriculumDraft, initialCourseSummary, level1Name]);

    const saveToHistory = () => {
        setHistory([...history, JSON.stringify({ curriculum, summary, courseOutcomes, programOutcomes, programSpecificOutcomes })]);
    };

    // Fix: Deep Clone ensures Module Descriptions and fields save properly
    const updateModuleField = (mIdx, field, value) => {
        saveToHistory();
        const newCur = structuredClone(curriculum);
        newCur[mIdx][field] = value;
        setCurriculum(newCur);
    };

    const updateLOField = (mIdx, loIdx, field, value) => {
        saveToHistory();
        const newCur = structuredClone(curriculum);
        newCur[mIdx].learning_outcomes[loIdx][field] = value;
        setCurriculum(newCur);
    };

    const handleSaveDraft = async () => {
        setSaving(true);
        try {
            const user = getCurrentUser();
            const slug = user?.tenant_slug;

            // Prepare the flat array for the backend
            const curriculumToSave = structuredClone(curriculum);
            
            // "Hack": Hide the CO/PO data inside the first module so it doesn't break the DB schema
            // but successfully reloads when the user refreshes the page.
            if (curriculumToSave.length > 0) {
                curriculumToSave[0]._outcomes_backup = {
                    courseOutcomes,
                    programOutcomes,
                    programSpecificOutcomes,
                    courseSummary: summary
                };
            }

            const payload = {
                curriculum: curriculumToSave, // Passed as a flat array to protect Python backend
                course_summary: summary
            };

            await api.post(`/tenant/${slug}/save-curriculum-json/${projectId}`, payload);

            dispatch(addNotification({ id: Date.now(), type: 'success', message: 'Draft saved successfully!' }));
        } catch (error) {
            console.error("Failed to save draft:", error);
            dispatch(addNotification({ id: Date.now(), type: 'danger', message: 'Failed to save draft.' }));
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async () => {
        try {
            setSaving(true);
            const user = getCurrentUser();
            const slug = user?.tenant_slug;
            
            const curriculumToSave = structuredClone(curriculum);
            
            // Safety Net: If they deleted all modules, create an empty one
            if (curriculumToSave.length === 0) {
                curriculumToSave.push({ title: `New ${level1Name}`, children: [], learning_outcomes: [] });
            }

            // Hide the CO/PO data and Summary inside the first module
            curriculumToSave[0]._outcomes_backup = {
                courseOutcomes, 
                programOutcomes, 
                programSpecificOutcomes,
                courseSummary: summary
            };

            // STEP 1: Save the final JSON state to the database
            const payload = {
                curriculum: curriculumToSave,
                course_summary: summary
            };
            await api.post(`/tenant/${slug}/save-curriculum-json/${projectId}`, payload);

            // STEP 2: Fetch the reviews to find the pending workflow ID
            const reviewRes = await api.get(`/tenant/${slug}/reviews?project_id=${projectId}`);
            const pending = reviewRes.data.find(r => r.stage === 'curriculum_mapping' && r.status === 'pending');
            
            // STEP 3: Trigger the approval workflow on the backend
            if (pending) {
                await api.post(`/tenant/${slug}/approve-review/${pending.id}`, { 
                    comments: "SME Finalized curriculum structure." 
                });
            } else {
                console.warn("Draft saved, but no pending review was found to approve.");
            }

            // STEP 4: Notify the parent component to close the editor / update UI
            if (onApprove) onApprove();
            
            dispatch(addNotification({ id: Date.now(), type: 'success', message: 'Curriculum mapping approved!' }));
        } catch (error) {
            console.error("Failed to approve curriculum:", error);
            dispatch(addNotification({ id: Date.now(), type: 'danger', message: 'Failed to approve curriculum.' }));
        } finally {
            setSaving(false);
        }
    };

    const BLOOM_LEVELS = [
        "L1 - Remember", "L2 - Understand", "L3 - Apply", 
        "L4 - Analyze", "L5 - Evaluate", "L6 - Create"
    ];

    return (
        <div>
            {/* TOP CONTROLS */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold mb-0">Curriculum Mapping</h4>
                <div className="d-flex gap-2">
                    <Button variant="outline-dark" onClick={handleSaveDraft} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" /> : null} Save Draft
                    </Button>
                    <Button variant="success" onClick={handleApprove} disabled={saving}>
                        Approve
                    </Button>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════════════════ */}
            {/* COURSE OVERVIEW & OUTCOME DEFINITIONS */}
            {/* ═══════════════════════════════════════════════════════════════════════════ */}
            <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-white py-3">
                    <h5 className="mb-0 fw-bold">Course Overview</h5>
                </Card.Header>
                <Card.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Course Summary & Rationale</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={3} 
                            value={summary}
                            onChange={(e) => {
                                saveToHistory();
                                setSummary(e.target.value);
                            }}
                        />
                    </Form.Group>

                    {showAcademicMapping && (
                        <Row className="mt-4 border-top pt-4">
                            {/* CO COLUMN */}
                            <Col md={4}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold text-primary mb-0">Course Outcomes (CO)</h6>
                                    <Button variant="outline-primary" size="sm" className="py-0 px-2" onClick={() => {
                                        saveToHistory();
                                        setCourseOutcomes([...courseOutcomes, { code: `CO${courseOutcomes.length + 1}`, text: '' }]);
                                    }}>+ Add</Button>
                                </div>
                                {courseOutcomes.map((co, idx) => (
                                    <InputGroup size="sm" className="mb-2" key={idx}>
                                        <InputGroup.Text className="bg-light fw-bold text-secondary" style={{ width: '60px', justifyContent: 'center' }}>{co.code}</InputGroup.Text>
                                        <Form.Control value={co.text} onChange={(e) => {
                                            saveToHistory();
                                            const newCOs = [...courseOutcomes];
                                            newCOs[idx].text = e.target.value;
                                            setCourseOutcomes(newCOs);
                                        }} />
                                        <Button variant="outline-danger" onClick={() => {
                                            saveToHistory();
                                            const newCOs = [...courseOutcomes];
                                            newCOs.splice(idx, 1);
                                            setCourseOutcomes(newCOs.map((item, i) => ({ ...item, code: `CO${i + 1}` })));
                                        }}><Trash2 size={14} /></Button>
                                    </InputGroup>
                                ))}
                            </Col>
                            
                            {/* PO COLUMN */}
                            <Col md={4}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold text-success mb-0">Program Outcomes (PO)</h6>
                                    <Button variant="outline-success" size="sm" className="py-0 px-2" onClick={() => {
                                        saveToHistory();
                                        setProgramOutcomes([...programOutcomes, { code: `PO${programOutcomes.length + 1}`, text: '' }]);
                                    }}>+ Add</Button>
                                </div>
                                {programOutcomes.map((po, idx) => (
                                    <InputGroup size="sm" className="mb-2" key={idx}>
                                        <InputGroup.Text className="bg-light fw-bold text-secondary" style={{ width: '60px', justifyContent: 'center' }}>{po.code}</InputGroup.Text>
                                        <Form.Control value={po.text} onChange={(e) => {
                                            saveToHistory();
                                            const newPOs = [...programOutcomes];
                                            newPOs[idx].text = e.target.value;
                                            setProgramOutcomes(newPOs);
                                        }} />
                                        <Button variant="outline-danger" onClick={() => {
                                            saveToHistory();
                                            const newPOs = [...programOutcomes];
                                            newPOs.splice(idx, 1);
                                            setProgramOutcomes(newPOs.map((item, i) => ({ ...item, code: `PO${i + 1}` })));
                                        }}><Trash2 size={14} /></Button>
                                    </InputGroup>
                                ))}
                            </Col>

                            {/* PSO COLUMN */}
                            <Col md={4}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold text-info mb-0">Program Specific (PSO)</h6>
                                    <Button variant="outline-info" size="sm" className="py-0 px-2" onClick={() => {
                                        saveToHistory();
                                        setProgramSpecificOutcomes([...programSpecificOutcomes, { code: `PSO${programSpecificOutcomes.length + 1}`, text: '' }]);
                                    }}>+ Add</Button>
                                </div>
                                {programSpecificOutcomes.map((pso, idx) => (
                                    <InputGroup size="sm" className="mb-2" key={idx}>
                                        <InputGroup.Text className="bg-light fw-bold text-secondary" style={{ width: '60px', justifyContent: 'center' }}>{pso.code}</InputGroup.Text>
                                        <Form.Control value={pso.text} onChange={(e) => {
                                            saveToHistory();
                                            const newPSOs = [...programSpecificOutcomes];
                                            newPSOs[idx].text = e.target.value;
                                            setProgramSpecificOutcomes(newPSOs);
                                        }} />
                                        <Button variant="outline-danger" onClick={() => {
                                            saveToHistory();
                                            const newPSOs = [...programSpecificOutcomes];
                                            newPSOs.splice(idx, 1);
                                            setProgramSpecificOutcomes(newPSOs.map((item, i) => ({ ...item, code: `PSO${i + 1}` })));
                                        }}><Trash2 size={14} /></Button>
                                    </InputGroup>
                                ))}
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* ═══════════════════════════════════════════════════════════════════════════ */}
            {/* CURRICULUM TREE EDITOR WITH MODULE METADATA RESTORED */}
            {/* ═══════════════════════════════════════════════════════════════════════════ */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white py-3">
                    <h5 className="mb-0 fw-bold">Curriculum Structure</h5>
                </Card.Header>
                <Card.Body>
                    {curriculum.map((module, mIdx) => (
                        <Card key={mIdx} className="mb-4 border border-2">
                            <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2">
                                <Form.Control 
                                    value={module.title}
                                    onChange={(e) => updateModuleField(mIdx, 'title', e.target.value)}
                                    className="fw-bold border-0 bg-transparent shadow-none px-0 text-primary"
                                    style={{ fontSize: '1.2rem' }}
                                />
                                <Button variant="link" className="text-danger p-0 ms-3" onClick={() => {
                                    saveToHistory();
                                    const newCur = structuredClone(curriculum);
                                    newCur.splice(mIdx, 1);
                                    setCurriculum(newCur);
                                }}><Trash2 size={18} /></Button>
                            </Card.Header>
                            
                            <Card.Body className="p-3">
                                {module.module_average_cognitive_score && (
                                    <div className="mb-3">
                                        <Badge bg="info" text="dark" className="px-3 py-2 border border-info shadow-sm">
                                            <i className="bi bi-brain me-2"></i>
                                            Avg. Cognitive Score: {module.module_average_cognitive_score}
                                        </Badge>
                                    </div>
                                )}

                                <Form.Group className="mb-4">
                                    <Form.Label className="small fw-bold text-muted text-uppercase mb-1">Module Description</Form.Label>
                                    <Form.Control 
                                        as="textarea" rows={2} 
                                        value={module.description}
                                        onChange={(e) => updateModuleField(mIdx, 'description', e.target.value)}
                                    />
                                </Form.Group>

                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="small fw-bold text-muted text-uppercase mb-0">Module Level Learning Outcomes (LO)</Form.Label>
                                        <Button size="sm" variant="outline-primary" style={{ fontSize: '0.75rem' }} onClick={() => {
                                            saveToHistory();
                                            const newCur = structuredClone(curriculum);
                                            newCur[mIdx].learning_outcomes.push({ text: '', bloom_level: '', mapped_co: '', mapped_po: '', mapped_pso: '' });
                                            setCurriculum(newCur);
                                        }}>+ Add LO</Button>
                                    </div>
                                    
                                    {module.learning_outcomes.map((lo, loIdx) => (
                                        <div className="p-3 bg-light border rounded mb-2" key={loIdx}>
                                            <div className="d-flex gap-2 mb-2">
                                                <InputGroup size="sm">
                                                    <InputGroup.Text className="fw-bold bg-white text-primary">LO {loIdx + 1}</InputGroup.Text>
                                                    <Form.Control 
                                                        placeholder="Describe the learning outcome..." 
                                                        value={lo.text}
                                                        onChange={(e) => updateLOField(mIdx, loIdx, 'text', e.target.value)}
                                                    />
                                                </InputGroup>
                                                <Button variant="outline-danger" size="sm" onClick={() => {
                                                    saveToHistory();
                                                    const newCur = structuredClone(curriculum);
                                                    newCur[mIdx].learning_outcomes.splice(loIdx, 1);
                                                    setCurriculum(newCur);
                                                }}><Trash2 size={14} /></Button>
                                            </div>

                                            {showAcademicMapping && (
                                                <Row className="gx-2">
                                                    <Col md={3}>
                                                        <Form.Select size="sm" 
                                                            value={(lo.bloom_level || '').split(' ')[0]}
                                                            onChange={(e) => updateLOField(mIdx, loIdx, 'bloom_level', e.target.value)}
                                                        >
                                                            <option value="">Bloom's Level</option>
                                                            {BLOOM_LEVELS.map(b => <option key={b} value={b.split(' ')[0]}>{b}</option>)}
                                                        </Form.Select>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control size="sm" placeholder="Mapped COs (e.g. CO1, CO2)" value={lo.mapped_co} onChange={(e) => updateLOField(mIdx, loIdx, 'mapped_co', e.target.value)} />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control size="sm" placeholder="Mapped POs (e.g. PO1)" value={lo.mapped_po} onChange={(e) => updateLOField(mIdx, loIdx, 'mapped_po', e.target.value)} />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control size="sm" placeholder="Mapped PSOs" value={lo.mapped_pso} onChange={(e) => updateLOField(mIdx, loIdx, 'mapped_pso', e.target.value)} />
                                                    </Col>
                                                </Row>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <Form.Group className="mb-4">
                                    <Form.Label className="small fw-bold text-muted text-uppercase mb-1">Skills & Competencies</Form.Label>
                                    <Form.Control 
                                        as="textarea" rows={2} 
                                        value={module.skills_competencies}
                                        onChange={(e) => updateModuleField(mIdx, 'skills_competencies', e.target.value)}
                                    />
                                </Form.Group>

                                <hr className="my-4 text-muted" />

                                <h6 className="small fw-bold text-muted text-uppercase mb-3">Units & Topics</h6>
                                <Accordion>
                                    {module.children && module.children.map((unit, uIdx) => (
                                        <Accordion.Item eventKey={uIdx.toString()} key={uIdx} className="mb-2 border">
                                            <Accordion.Header>
                                                <div className="w-100 me-3" onClick={(e) => e.stopPropagation()}>
                                                    <Form.Control 
                                                        size="sm"
                                                        value={unit.title}
                                                        onChange={(e) => {
                                                            saveToHistory();
                                                            const newCur = structuredClone(curriculum);
                                                            newCur[mIdx].children[uIdx].title = e.target.value;
                                                            setCurriculum(newCur);
                                                        }}
                                                        className="fw-semibold border-0 bg-transparent shadow-none px-0"
                                                    />
                                                </div>
                                                <Button variant="link" className="text-danger p-0 me-2" onClick={(e) => {
                                                    e.stopPropagation();
                                                    saveToHistory();
                                                    const newCur = structuredClone(curriculum);
                                                    newCur[mIdx].children.splice(uIdx, 1);
                                                    setCurriculum(newCur);
                                                }}><Trash2 size={15} /></Button>
                                            </Accordion.Header>
                                            <Accordion.Body className="p-3 bg-light">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 className="mb-0 text-muted small fw-bold text-uppercase">{level3Name}s</h6>
                                                    <Button variant="outline-secondary" size="sm" className="py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={() => {
                                                        saveToHistory();
                                                        const newCur = structuredClone(curriculum);
                                                        if(!newCur[mIdx].children[uIdx].topics) newCur[mIdx].children[uIdx].topics = [];
                                                        newCur[mIdx].children[uIdx].topics.push(`New ${level3Name}`);
                                                        setCurriculum(newCur);
                                                    }}>+ Add</Button>
                                                </div>
                                                {unit.topics && unit.topics.map((topic, tIdx) => (
                                                    <div className="d-flex align-items-center gap-2 mb-2" key={tIdx}>
                                                        <Form.Control 
                                                            size="sm"
                                                            value={typeof topic === 'object' ? topic.title || topic.name : topic}
                                                            onChange={(e) => {
                                                                saveToHistory();
                                                                const newCur = structuredClone(curriculum);
                                                                newCur[mIdx].children[uIdx].topics[tIdx] = e.target.value;
                                                                setCurriculum(newCur);
                                                            }}
                                                        />
                                                        <Button variant="link" size="sm" className="text-danger p-0" onClick={() => {
                                                            saveToHistory();
                                                            const newCur = structuredClone(curriculum);
                                                            newCur[mIdx].children[uIdx].topics.splice(tIdx, 1);
                                                            setCurriculum(newCur);
                                                        }}><Trash2 size={15} /></Button>
                                                    </div>
                                                ))}
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                                <Button variant="outline-secondary" size="sm" className="w-100 mt-2" style={{ borderStyle: 'dashed' }} onClick={() => {
                                    saveToHistory();
                                    const newCur = structuredClone(curriculum);
                                    if (!newCur[mIdx].children) newCur[mIdx].children = [];
                                    
                                    // 🟢 HYBRID FIX: Find the highest existing ID in this module to prevent deletion collisions
                                    const existingIds = newCur[mIdx].children
                                        .map(child => Number(child.unit_id))
                                        .filter(id => !isNaN(id)); // Filter out any bad data
                                        
                                    // If there are existing units, take the highest ID and add 1. 
                                    // If empty, start at the baseline (e.g., Module 1 = 100, so first unit is 101)
                                    const baseId = (mIdx + 1) * 100;
                                    const nextUnitId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : baseId + 1;
                                    
                                    newCur[mIdx].children.push({ 
                                        unit_id: nextUnitId, 
                                        title: `New ${level2Name}`, 
                                        topics: [] 
                                    });
                                    setCurriculum(newCur);
                                }}>+ Add New {level2Name}</Button>
                            </Card.Body>
                        </Card>
                    ))}
                    <Button variant="primary" className="w-100 py-3 fw-bold mt-2 shadow-sm" onClick={() => {
                        saveToHistory();
                        setCurriculum([...curriculum, { 
                            title: `New ${level1Name}`, 
                            children: [], 
                            learning_outcomes: [], 
                            description: "", 
                            skills_competencies: "" 
                        }]);
                    }}>+ Add New {level1Name}</Button>
                </Card.Body>
            </Card>

            <Row className="mt-2 mb-5">
                <Col md={6}>
                    <Button 
                        variant="outline-dark" 
                        className="w-100 py-3 fw-bold shadow-sm" 
                        onClick={handleSaveDraft} 
                        disabled={saving}
                        style={{ fontSize: '1.1rem' }}
                    >
                        {saving ? <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" /> : null}
                        Save Draft
                    </Button>
                </Col>
                <Col md={6}>
                    <Button 
                        variant="success" 
                        className="w-100 py-3 fw-bold shadow-sm" 
                        onClick={handleApprove} 
                        disabled={saving}
                        style={{ fontSize: '1.1rem' }}
                    >
                        {saving ? <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" /> : null}
                        {saving ? 'Approving...' : 'Approve Curriculum Mapping'}
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

export default CurriculumMappingEditor;